import { INDIA_COUNTRY_CODE } from "./countries";

/** Which price list a visitor sees before a billing address exists. */
export type PricingRegion = "IN" | "INTERNATIONAL";

/**
 * Currency each region is charged in. Both plans exist and the API returns both to anonymous
 * callers, so this now selects a real plan rather than expressing an intention.
 *
 * There is exactly one non-INR plan: every customer outside India is billed in **US dollars**,
 * including markets with their own dollar. Nothing here is per-country.
 */
export const CURRENCY_BY_REGION: Record<PricingRegion, string> = {
	IN: "INR",
	INTERNATIONAL: "USD"
};

/**
 * IANA time zone to ISO 3166-1 alpha-2, covering the zones our visitors actually use. This is a
 * display hint only — it prefills the billing country and picks which price to show before an
 * address is on file. The saved billing country, not this, decides what a customer is charged and
 * how the sale is taxed. A VPN or an unlisted zone just means no prefill, never a wrong charge.
 */
const COUNTRY_BY_TIME_ZONE: Record<string, string> = {
	"Africa/Accra": "GH",
	"Africa/Cairo": "EG",
	"Africa/Johannesburg": "ZA",
	"Africa/Lagos": "NG",
	"Africa/Nairobi": "KE",
	"America/Anchorage": "US",
	"America/Argentina/Buenos_Aires": "AR",
	"America/Bogota": "CO",
	"America/Chicago": "US",
	"America/Denver": "US",
	"America/Detroit": "US",
	"America/Edmonton": "CA",
	"America/Halifax": "CA",
	"America/Indiana/Indianapolis": "US",
	"America/Lima": "PE",
	"America/Los_Angeles": "US",
	"America/Mexico_City": "MX",
	"America/New_York": "US",
	"America/Phoenix": "US",
	"America/Santiago": "CL",
	"America/Sao_Paulo": "BR",
	"America/Toronto": "CA",
	"America/Vancouver": "CA",
	"America/Winnipeg": "CA",
	"Asia/Bangkok": "TH",
	"Asia/Calcutta": "IN",
	"Asia/Colombo": "LK",
	"Asia/Dhaka": "BD",
	"Asia/Dubai": "AE",
	"Asia/Ho_Chi_Minh": "VN",
	"Asia/Hong_Kong": "HK",
	"Asia/Jakarta": "ID",
	"Asia/Jerusalem": "IL",
	"Asia/Karachi": "PK",
	"Asia/Kathmandu": "NP",
	"Asia/Kolkata": "IN",
	"Asia/Kuala_Lumpur": "MY",
	"Asia/Kuwait": "KW",
	"Asia/Manila": "PH",
	"Asia/Qatar": "QA",
	"Asia/Riyadh": "SA",
	"Asia/Saigon": "VN",
	"Asia/Seoul": "KR",
	"Asia/Shanghai": "CN",
	"Asia/Singapore": "SG",
	"Asia/Taipei": "TW",
	"Asia/Tehran": "IR",
	"Asia/Tokyo": "JP",
	"Australia/Adelaide": "AU",
	"Australia/Brisbane": "AU",
	"Australia/Melbourne": "AU",
	"Australia/Perth": "AU",
	"Australia/Sydney": "AU",
	"Europe/Amsterdam": "NL",
	"Europe/Athens": "GR",
	"Europe/Berlin": "DE",
	"Europe/Brussels": "BE",
	"Europe/Bucharest": "RO",
	"Europe/Budapest": "HU",
	"Europe/Copenhagen": "DK",
	"Europe/Dublin": "IE",
	"Europe/Helsinki": "FI",
	"Europe/Istanbul": "TR",
	"Europe/Kiev": "UA",
	"Europe/Kyiv": "UA",
	"Europe/Lisbon": "PT",
	"Europe/London": "GB",
	"Europe/Madrid": "ES",
	"Europe/Moscow": "RU",
	"Europe/Oslo": "NO",
	"Europe/Paris": "FR",
	"Europe/Prague": "CZ",
	"Europe/Rome": "IT",
	"Europe/Stockholm": "SE",
	"Europe/Vienna": "AT",
	"Europe/Warsaw": "PL",
	"Europe/Zurich": "CH",
	"Pacific/Auckland": "NZ",
	"Pacific/Honolulu": "US"
};

/** The browser's IANA time zone, or an empty string where it cannot be read. */
export const getBrowserTimeZone = (): string => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
	} catch {
		return "";
	}
};

/** Alpha-2 code for an IANA zone, or null when the zone is not in the lookup above. */
export const getCountryCodeFromTimeZone = (timeZone: string): string | null => COUNTRY_BY_TIME_ZONE[timeZone] ?? null;

/** Best guess at the visitor's country from their time zone. Null when it cannot be determined. */
export const detectCountryCode = (): string | null => getCountryCodeFromTimeZone(getBrowserTimeZone());

/**
 * Which price list to show a visitor who has not signed in. Anything not positively identified as
 * India is treated as international.
 *
 * The old rationale here — that an unlisted zone was harmless "because the INR plan is the only one
 * the API returns" — stopped being true once the plans endpoint began returning both (TTAPI-17). An
 * unmapped zone now shows US dollars rather than falling back to rupees, so the tradeoff is worth
 * stating plainly:
 *
 * - Both Indian zones (`Asia/Calcutta`, `Asia/Kolkata`) are in the lookup, so a real Indian visitor
 *   is identified and sees ₹59. The exposure is limited to genuinely exotic zones.
 * - When it does misfire on an Indian visitor, they are quoted $1 and charged ₹59 — quoted *above*
 *   what they pay. The reverse default would quote ₹59 to an international visitor who is then
 *   debited $1, which is the mismatch TTAPI-17 existed to remove.
 *
 * Either way this is only a display hint. The saved billing country decides what is actually
 * charged, and the server resolves that independently — see SubscriptionPlanResolver.
 */
export const detectPricingRegion = (): PricingRegion =>
	detectCountryCode() === INDIA_COUNTRY_CODE ? "IN" : "INTERNATIONAL";
